import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Bold, Italic, Underline, Strikethrough, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, Link as LinkIcon, Undo, Redo,
  Type, Minus,
} from 'lucide-react';

// Full-featured Rich Text Editor Component
// Full-featured Rich Text Editor Component
const RichTextEditor = ({ value, onChange, placeholder, minHeight = '200px' }) => {
  const editorRef = useRef(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  // Update content when value changes externally
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const execCommand = (cmd, value = null) => {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
    handleChange();
  };

  const handleChange = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const insertLink = () => {
    if (linkUrl) {
      execCommand('createLink', linkUrl);
      setLinkUrl('');
      setShowLinkDialog(false);
    }
  };

  const ToolbarButton = ({ onClick, active, children, title }) => (
    <button
      type="button"
      onClick={onClick}
      className={`p-1.5 rounded hover:bg-stone-200 ${active ? 'bg-stone-200' : ''}`}
      title={title}
    >
      {children}
    </button>
  );

  const ToolbarDivider = () => <div className="w-px h-6 bg-stone-300 mx-1" />;

  return (
    <div className="border border-stone-300 bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-stone-200 bg-stone-50">
        {/* Undo/Redo */}
        <ToolbarButton onClick={() => execCommand('undo')} title="Ångra (Ctrl+Z)">
          <Undo className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('redo')} title="Gör om (Ctrl+Y)">
          <Redo className="w-4 h-4" />
        </ToolbarButton>
        
        <ToolbarDivider />
        
        {/* Text formatting */}
        <ToolbarButton onClick={() => execCommand('bold')} title="Fet (Ctrl+B)">
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('italic')} title="Kursiv (Ctrl+I)">
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('underline')} title="Understruken (Ctrl+U)">
          <Underline className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('strikeThrough')} title="Genomstruken">
          <Strikethrough className="w-4 h-4" />
        </ToolbarButton>
        
        <ToolbarDivider />
        
        {/* Headings */}
        <select
          onChange={(e) => execCommand('formatBlock', e.target.value)}
          className="px-2 py-1 text-sm border border-stone-200 rounded bg-white h-8"
          defaultValue="p"
        >
          <option value="p">Normal</option>
          <option value="h1">Rubrik 1</option>
          <option value="h2">Rubrik 2</option>
          <option value="h3">Rubrik 3</option>
          <option value="h4">Rubrik 4</option>
          <option value="blockquote">Citat</option>
        </select>
        
        {/* Font size */}
        <select
          onChange={(e) => execCommand('fontSize', e.target.value)}
          className="px-2 py-1 text-sm border border-stone-200 rounded bg-white h-8 ml-1"
          defaultValue="3"
        >
          <option value="1">Liten</option>
          <option value="2">Mindre</option>
          <option value="3">Normal</option>
          <option value="4">Större</option>
          <option value="5">Stor</option>
          <option value="6">Mycket stor</option>
          <option value="7">Störst</option>
        </select>
        
        <ToolbarDivider />
        
        {/* Text color */}
        <div className="relative">
          <input
            type="color"
            onChange={(e) => execCommand('foreColor', e.target.value)}
            className="w-8 h-8 cursor-pointer border border-stone-200 rounded"
            title="Textfärg"
            defaultValue="#000000"
          />
        </div>
        
        {/* Background color */}
        <div className="relative">
          <input
            type="color"
            onChange={(e) => execCommand('hiliteColor', e.target.value)}
            className="w-8 h-8 cursor-pointer border border-stone-200 rounded"
            title="Bakgrundsfärg"
            defaultValue="#ffffff"
          />
        </div>
        
        <ToolbarDivider />
        
        {/* Lists */}
        <ToolbarButton onClick={() => execCommand('insertUnorderedList')} title="Punktlista">
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('insertOrderedList')} title="Numrerad lista">
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        
        <ToolbarDivider />
        
        {/* Alignment */}
        <ToolbarButton onClick={() => execCommand('justifyLeft')} title="Vänsterjustera">
          <AlignLeft className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('justifyCenter')} title="Centrera">
          <AlignCenter className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton onClick={() => execCommand('justifyRight')} title="Högerjustera">
          <AlignRight className="w-4 h-4" />
        </ToolbarButton>
        
        <ToolbarDivider />
        
        {/* Link */}
        <ToolbarButton onClick={() => setShowLinkDialog(true)} title="Infoga länk">
          <LinkIcon className="w-4 h-4" />
        </ToolbarButton>
        
        {/* Horizontal line */}
        <ToolbarButton onClick={() => execCommand('insertHorizontalRule')} title="Horisontell linje">
          <Minus className="w-4 h-4" />
        </ToolbarButton>
        
        {/* Clear formatting */}
        <ToolbarButton onClick={() => execCommand('removeFormat')} title="Ta bort formatering">
          <Type className="w-4 h-4 text-red-500" />
        </ToolbarButton>
      </div>
      
      {/* Link dialog */}
      {showLinkDialog && (
        <div className="p-3 border-b border-stone-200 bg-[#f5f0f2] flex items-center gap-2">
          <Input
            type="url"
            placeholder="https://example.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            className="flex-1 h-8 text-sm"
          />
          <Button type="button" size="sm" onClick={insertLink} className="h-8">Infoga</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setShowLinkDialog(false)} className="h-8">Avbryt</Button>
        </div>
      )}
      
      {/* Editor content */}
      <div
        ref={editorRef}
        contentEditable
        className="p-4 focus:outline-none prose prose-stone max-w-none overflow-auto"
        style={{ minHeight }}
        onInput={handleChange}
        onBlur={handleChange}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
      
      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        [contenteditable] h1 { font-size: 2em; font-weight: bold; margin: 0.5em 0; }
        [contenteditable] h2 { font-size: 1.5em; font-weight: bold; margin: 0.5em 0; }
        [contenteditable] h3 { font-size: 1.25em; font-weight: bold; margin: 0.5em 0; }
        [contenteditable] h4 { font-size: 1.1em; font-weight: bold; margin: 0.5em 0; }
        [contenteditable] ul, [contenteditable] ol { margin-left: 1.5em; margin-top: 0.5em; margin-bottom: 0.5em; }
        [contenteditable] ul { list-style-type: disc; }
        [contenteditable] ol { list-style-type: decimal; }
        [contenteditable] blockquote { border-left: 3px solid #d1d5db; padding-left: 1em; margin: 0.5em 0; color: #6b7280; }
        [contenteditable] a { color: #321a27; text-decoration: underline; }
        [contenteditable] hr { border: none; border-top: 1px solid #d1d5db; margin: 1em 0; }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
